import { useSyncExternalStore } from "react";

import type { AvatarLook } from "@/shared/types";

/** What the store holds: the seed, and the saved look that overrides it. */
interface UserAvatarState {
  seed: string;
  avatar: AvatarLook | null;
}

/* ----------------------------------------------------------------------------
   The signed-in user's character, as a subscribable store.

   Ceiling: browser-local, server field when the profile API grows one.
   `/settings` carries no avatar, so the look lives in localStorage keyed by
   user id — a shared browser must not hand one person another's face. That is
   why the store has to be pointed at a user (`loadUserAvatar`) once the boot
   query resolves, rather than reading itself at import time.

   Client state, not server state, so it is a plain store and not a query
   (rule book §3 rule 7). `useSyncExternalStore` is what makes saving your
   avatar update the sidebar in the same frame.
---------------------------------------------------------------------------- */

const keyFor = (userId: string) => `definable-avatar:${userId}`;

let userId = "";
let state: UserAvatarState = { seed: "", avatar: null };

const subscribers = new Set<() => void>();
const notify = () => subscribers.forEach((fn) => fn());

const restore = (id: string): UserAvatarState | null => {
  try {
    return JSON.parse(localStorage.getItem(keyFor(id)) || "null");
  } catch {
    return null; // private mode / corrupt value — fall back to the seeded look
  }
};

/** Point the store at the signed-in user. Idempotent: the boot gate calls it on
    every settings refresh. The user id is the default seed, so two accounts
    never draw the same character by accident. */
export function loadUserAvatar(id: string): void {
  if (id === userId) return;
  userId = id;
  const saved = restore(id);
  state = { seed: saved?.seed || id, avatar: saved?.avatar ?? null };
  notify();
}

export const getUserAvatar = (): UserAvatarState => state;

export function setUserAvatar(next: Partial<UserAvatarState>): void {
  state = { ...state, ...next };
  try {
    if (userId) localStorage.setItem(keyFor(userId), JSON.stringify(state));
  } catch {
    /* storage blocked — the avatar still applies for this session */
  }
  notify();
}

export function subscribeUserAvatar(fn: () => void): () => void {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

export const useUserAvatar = (): UserAvatarState =>
  useSyncExternalStore(subscribeUserAvatar, getUserAvatar);
